import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Button, Modal, Table, Tag} from 'antd'
import {getCoverageSummary, getStages} from '../../../../services/api/apiProductionPlanning'
import {ROUTES, RATE_UNIT_LABELS, stageConfigPath} from '../../constants'
import {canImportStandardConfig} from '../../helpers/permission'
import queryString from 'query-string'
import {omitUrlParams, setUrlParams} from '../../../../share/urlParams'
import {DASH, formatDateTime, hasReadableOffset, timezoneLabelAt, formatInt, formatRate} from '../../helpers/format'
import Notice from '../shared/Notice'
import SkuIdList from '../shared/SkuIdList'
import LoadError from '../shared/LoadError'
import ActionCodes from '../shared/ActionCodes'
import {CountUnitTag, PacingTag, StageTypeTag, TimeBasisTag} from '../shared/StageBadges'
import LookupPage from './LookupPage'
import ImportExportPage from './ImportExportPage'

/**
 * SCR-M16-001 — Chỉ số tiêu chuẩn theo công đoạn.
 *
 * Không có khối thẻ thống kê: vị trí đầu màn dành cho **cảnh báo bấm được**. Thẻ thống kê chỉ cho
 * biết con số, còn cảnh báo dẫn thẳng tới việc cần làm (§2).
 *
 * Không phân trang, không sắp xếp. Thứ tự cố định: theo quy trình → bắt buộc chung → theo yêu cầu.
 */
function StageListPage({history, location}) {
    /*
     * Hai màn "Tra cứu định mức theo SKU" và "Nhập / xuất Excel" KHÔNG còn mục menu và cũng không
     * còn là route riêng — chúng là modal mở từ đây.
     *
     * Trạng thái đặt trên URL (`lookup`, `mode`) chứ không phải `useState`, cùng cơ chế với các
     * modal của màn Sản lượng: copy đường dẫn gửi cho người khác là họ mở ra đúng thứ mình đang
     * xem. `LookupPage` tự quản `?sku=` của riêng nó.
     *
     * URL là NGUỒN DUY NHẤT — đọc thẳng `location`, ghi thẳng `history`, không giữ bản sao trong
     * state.
     *
     * ⚠️ Không dùng `useUrlFilters` ở đây, dù nó có vẻ đúng việc. Hook đó giữ `values` là state nội
     *    bộ chốt lúc mount và chỉ đồng bộ MỘT CHIỀU state → URL: bấm Back đổi URL nhưng state không
     *    đổi, nên effect của hook lập tức ghi giá trị cũ đè lại. Hệ quả đo được: Back không đóng
     *    được modal (URL bị đẩy về `?lookup=1` ngay), và nếu khai `sku` vào defaults thì cha còn
     *    xoá mất `?sku=` mà `LookupPage` vừa đặt rồi hai bên đẩy qua đẩy lại.
     *
     *    Đọc thẳng URL cũng gỡ luôn rủi ro hai instance `useUrlFilters` sống trên cùng một
     *    `location`: cả màn này lẫn `LookupPage` đều tự quản URL — màn này giữ `lookup`/`mode`,
     *    `LookupPage` giữ `sku`. Hai màn modal này không còn dùng hook; `MissingSkuPage` (cùng
     *    thư mục) thì vẫn dùng, nhưng nằm ở route riêng nên không đụng `location` này.
     *
     * Mở modal thì PUSH một entry: hồi hai màn này còn là route riêng, Back trả người dùng về đúng
     * danh sách công đoạn. Modal đặt `closable={false}` (không có ✕ ở góc) nên Back là phản xạ đầu
     * tiên để đóng — `replace` sẽ khiến Back rời hẳn màn hình.
     */
    const query = queryString.parse(location.search)
    const canImport = canImportStandardConfig()

    // Allow-list chứ không dùng `!!`: `queryString.parse('?lookup=0')` cho chuỗi "0", mà chuỗi "0"
    // là truthy — ai viết `?lookup=0` để TẮT sẽ được kết quả ngược. Nhận '1' và 'true' cho khớp
    // `parseUrlFilters` (`views/helpers/useUrlFilters.js`), và hạ hoa thường y như `mode` bên dưới —
    // link gõ tay viết hoa thì hai tham số cạnh nhau phải hành xử giống nhau.
    // Mảng (`?lookup=1&lookup=0`) rơi về '' ⇒ đóng: từ chối an toàn hơn đoán phần tử nào đúng.
    const rawLookup = query.lookup ? String(query.lookup).toLowerCase() : ''
    const lookupOpen = rawLookup === '1' || rawLookup === 'true'

    /*
     * `lookup` thắng `mode` khi URL có cả hai: hai Modal cùng `zIndex` và cùng `closable={false}`
     * nên mở chồng là người dùng thấy hai lớp đè nhau, không biết đóng cái nào. Redirect ở
     * `routes/view.js` đã dọn `mode` trước khi đặt `lookup` nên đường đó không sinh ra tình huống
     * này nữa; giữ lại đây làm lớp phòng cho URL sửa tay.
     *
     * Hạ `import` xuống `export` khi không có quyền: ẩn nút ở header là CHƯA đủ, vì `mode` đến từ
     * URL (share-link, bookmark cũ qua redirect). Không chặn ở đây thì người không có quyền vẫn đáp
     * xuống màn Nhập khoá cứng — đúng cái "bấm vào rồi mới báo lỗi" mà M16 §5.2 muốn tránh.
     */
    // `toLowerCase()` phòng link gõ tay viết hoa; `String()` biến mảng (`?mode=a&mode=b`) thành
    // chuỗi không khớp allow-list ⇒ từ chối, khác `parseUrlFilters` vốn lấy phần tử đầu.
    const rawMode = query.mode ? String(query.mode).toLowerCase() : ''
    const requestedMode = rawMode === 'export' || rawMode === 'import' ? rawMode : ''
    const allowedMode = requestedMode === 'import' && !canImport ? 'export' : requestedMode
    const ieMode = lookupOpen ? '' : allowedMode

    const pushModal = (patch) => history.push({
        pathname: location.pathname,
        search: setUrlParams(location.search, patch),
    })

    /*
     * Đóng bằng nút thì `goBack()` để HUỶ đúng entry vừa đẩy: vừa không đẻ thêm entry (không thì Back
     * sau khi đóng lại mở modal lên lần nữa), vừa dọn luôn `?sku=` vì nó nằm trong chính entry đó.
     * Người vào thẳng bằng share-link không có entry nào để huỷ — nhận ra bằng ref — nên tự dọn.
     */
    const pushedRef = useRef({lookup: false, ie: false})

    const openLookup = () => {
        pushedRef.current.lookup = true
        pushModal({lookup: '1'})
    }

    const openImportExport = (mode) => {
        pushedRef.current.ie = true
        pushModal({mode})
    }

    const closeModal = (keys, flag) => {
        if (pushedRef.current[flag]) {
            pushedRef.current[flag] = false
            history.goBack()

            return
        }

        history.replace({pathname: location.pathname, search: omitUrlParams(location.search, keys)})
    }

    const closeLookup = () => closeModal(['lookup', 'sku'], 'lookup')

    // Đổi tab thì REPLACE: người dùng lật qua lật lại giữa Nhập/Xuất không nên đẻ ra một chuỗi entry
    // mà Back phải bấm từng cái mới thoát khỏi modal.
    const changeIeMode = (mode) => history.replace({
        pathname: location.pathname,
        search: setUrlParams(location.search, {mode}),
    })

    const [stages, setStages] = useState([])
    const [summary, setSummary] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const fetchData = useCallback(async () => {
        setLoading(true)
        setError(null)

        try {
            // Độ phủ chạy song song và nên lưu tạm ở service (NFR-03): với 400 SKU × 6 công đoạn ×
            // 1–3 NVL thì số lần tra lên tới vài nghìn, không tính ở trình duyệt được.
            const [stageRows, coverage] = await Promise.all([getStages(), getCoverageSummary()])

            setStages(stageRows || [])
            setSummary(coverage)
        } catch (e) {
            setError(e)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const closeImportExport = () => closeModal(['mode'], 'ie')

    /*
     * Tải lại bảng mỗi khi modal Nhập/xuất ĐÓNG — bám vào `ieMode` chứ KHÔNG gắn vào handler nút.
     *
     * Lý do: modal đóng qua `visible={!!ieMode}`, tức URL điều khiển. Ngoài nút "Đóng"/ESC/bấm mask
     * còn một đường nữa là bấm Back, và chính màn này cố ý `push` một entry để Back đóng được modal.
     * Gắn `fetchData` vào handler thì đường Back không chạy qua nó.
     *
     * Cần tải lại vì import GHI ĐÈ toàn bộ nhóm của mỗi công đoạn, mà `fetchData` chỉ chạy lúc
     * mount. Hồi còn là route riêng, bấm "← Về danh sách" là remount nên số liệu tự mới; thành modal
     * thì không remount gì cả, bảng vẫn hiện groupCount / độ phủ / "Cập nhật lúc" của TRƯỚC khi
     * import — người dùng dễ tưởng import trượt và nạp lại lần nữa.
     */
    const prevIeModeRef = useRef(ieMode)

    useEffect(() => {
        if (prevIeModeRef.current && !ieMode) {
            fetchData()
        }

        prevIeModeRef.current = ieMode
    }, [ieMode, fetchData])

    const openStage = (stageCode) => history.push(stageConfigPath(stageCode))

    const columns = [
        {
            title: '#',
            key: 'index',
            width: 44,
            align: 'center',
            render: (text, row, index) => index + 1,
        },
        {
            title: 'Công đoạn',
            dataIndex: 'name',
            width: 150,
            render: (name) => <strong>{name}</strong>,
        },
        {
            title: 'Loại',
            dataIndex: 'stageType',
            width: 120,
            render: (stageType) => <StageTypeTag stageType={stageType}/>,
        },
        {
            title: 'Nhịp',
            dataIndex: 'pacing',
            width: 90,
            render: (pacing) => <PacingTag pacing={pacing}/>,
        },
        {
            title: 'Đơn vị đếm sản lượng',
            dataIndex: 'countUnit',
            width: 110,
            render: (countUnit) => <CountUnitTag countUnit={countUnit}/>,
        },
        {
            title: 'Cách tính giờ',
            key: 'timeBasis',
            width: 190,
            render: (text, row) => <TimeBasisTag timeBasis={row.timeBasis} detectField={row.detectField}/>,
        },
        {
            // Một công đoạn có thể ghi nhận NHIỀU mã (Đóng thùng: thường + thủ công) — `ActionCodes`
            // hiện đủ. Chưa chốt mã nào thì nó tự hiện dấu gạch (D-CR-05), không đoán.
            title: 'Action ghi nhận',
            dataIndex: 'actionCodes',
            width: 230,
            render: (codes, row) => <ActionCodes codes={codes} actionCode={row.actionCode}/>,
        },
        {
            title: 'Nhóm điều kiện',
            key: 'groups',
            width: 150,
            /*
             * `groupCount` = số nhóm đang QUẢN LÝ; `effectiveGroupCount` = số nhóm đang CHẠY ngay lúc
             * này. `hasAllGroup` và `rateRange` đều tính trên tập đang chạy, nên khi hai con số lệch
             * nhau mà chỉ in `groupCount` thì ô này tự mâu thuẫn: "1 nhóm — chưa có ALL" trong khi
             * nhóm duy nhất đó CHÍNH LÀ nhóm ALL, chỉ là chưa tới ngày hiệu lực. Người dùng kết luận
             * bảng hỏng rồi thêm nhóm ALL thứ hai — và `PUT` trả 400 VAL-09.
             */
            render: (text, row) => {
                if (!row.groupCount) {
                    return <Tag color="red">Chưa cấu hình</Tag>
                }

                const dormant = row.groupCount - (row.effectiveGroupCount || 0)

                return (
                    <span>
                        {row.groupCount} nhóm{' '}
                        {dormant > 0 && (
                            <span className="VcMuted">({dormant} chưa/không còn hiệu lực) </span>
                        )}
                        {row.hasAllGroup
                            ? <span className="VcMuted">+ ALL</span>
                            : <span className="VcDanger">— chưa có ALL đang chạy</span>}
                    </span>
                )
            },
        },
        {
            title: 'Khoảng chỉ số',
            key: 'rateRange',
            width: 185,
            render: (text, row) => {
                // `rateRange` tính trên nhóm ĐANG CHẠY. Có nhóm mà không có dải thì phải nói vì
                // sao, đừng để dấu gạch — dấu gạch ở cột này vốn có nghĩa "chưa cấu hình gì".
                if (!row.rateRange) {
                    return row.groupCount
                        ? <span className="VcMuted">chưa tới hiệu lực</span>
                        : <span className="VcMuted">{DASH}</span>
                }

                const unit = RATE_UNIT_LABELS[row.rateRange.unit]

                return (
                    <code className="VcRate">
                        {formatRate(row.rateRange.min)} – {formatRate(row.rateRange.max)} s/{unit}
                    </code>
                )
            },
        },
        {
            title: 'SKU đang bật thiếu định mức',
            dataIndex: 'missingSkuCount',
            width: 130,
            align: 'center',
            render: (missingSkuCount, row) => {
                // Công đoạn "theo yêu cầu" cảnh báo ở cấp công đoạn, không tính độ phủ SKU
                // (BR-M16-31): chỉ item/order được yêu cầu mới chụp ảnh.
                if (row.stageType === 'on_demand') {
                    return <span className="VcMuted">Không áp dụng</span>
                }

                return (
                    <Tag color={missingSkuCount > 0 ? 'red' : 'green'}>
                        {formatInt(missingSkuCount)} SKU
                    </Tag>
                )
            },
        },
        {
            title: '',
            key: 'action',
            width: 110,
            align: 'center',
            // Ghim phải: bảng rộng hơn khung nên phải cuộn ngang, nút không được trôi khỏi tầm mắt.
            fixed: 'right',
            render: (text, row) => (
                <Button
                    size="small"
                    type={row.missingSkuCount > 0 ? 'primary' : 'default'}
                    onClick={() => openStage(row.stageCode)}
                >
                    Cấu hình
                </Button>
            ),
        },
    ]

    const renderWarnings = () => {
        if (!summary) return null

        const {
            incompleteSkuCount,
            missingRoutingSkuCount,
            missingRoutingSkuIds,
            stagesWithoutGroup,
        } = summary

        const blocks = []

        // Thứ tự hiển thị W1 → W2 → W3, hiển thị đồng thời nếu cùng thỏa (§3.2).
        if (incompleteSkuCount > 0) {
            blocks.push(
                <Notice
                    key="w1"
                    level="error"
                    action="Xem danh sách →"
                    onAction={() => history.push(`${ROUTES.STANDARD_TIME_MISSING_SKU}?status=missing`)}
                >
                    {formatInt(incompleteSkuCount)} SKU đang bật thiếu định mức. Sản lượng của các SKU
                    này rơi vào no_standard ở công đoạn tương ứng.
                </Notice>
            )
        }

        // W2 tách khỏi W1: SKU chưa gán quy trình phải xử lý TRƯỚC, vì chưa có quy trình thì không
        // biết cần cấu hình những công đoạn nào (BR-M16-27). Nút cũng dẫn sang màn khác.
        if (missingRoutingSkuCount > 0) {
            blocks.push(
                <Notice
                    key="w2"
                    level="warning"
                    action="Xem danh sách →"
                    onAction={() => history.push(`${ROUTES.STANDARD_TIME_MISSING_SKU}?routing=__none__&status=all`)}
                >
                    <div>
                        {formatInt(missingRoutingSkuCount)} SKU đang bật chưa gán quy trình sản xuất.
                        Chưa xác định được cần định mức những công đoạn nào — xử lý ở màn Quản lý SKU.
                    </div>
                    <SkuIdList ids={missingRoutingSkuIds}/>
                </Notice>
            )
        }

        if ((stagesWithoutGroup || []).length) {
            blocks.push(
                <Notice key="w3" level="warning">
                    {stagesWithoutGroup.length} công đoạn chưa có nhóm cấu hình nào:{' '}
                    {stagesWithoutGroup.map((stage) => stage.name).join(', ')}
                </Notice>
            )
        }

        if (!blocks.length) {
            blocks.push(
                <Notice key="w4" level="success">Toàn bộ SKU đang bật đã đủ định mức.</Notice>
            )
        }

        return blocks
    }

    return (
        <div className="VcPage VcStageListPage container-fluid">
            <div className="VcPageHead">
                <div>
                    <h1 className="VcPageTitle">Chỉ số tiêu chuẩn theo công đoạn</h1>
                    <div className="VcPageSubtitle">
                        Mỗi công đoạn có một cấu hình, bên trong gồm nhiều nhóm điều kiện
                    </div>
                </div>
                <div className="VcPageActions">
                    {/*
                      * Chỉ icon, không kèm chữ — theo đúng cách các màn Home đang làm
                      * (`BtnRefresh fa fa-refresh` + title "Tải lại"). Dùng `loading` của antd để
                      * lúc đang tải thì icon đổi thành spinner và nút tự khoá, khỏi bấm chồng.
                      */}
                    <Button
                        className="VcBtnReload"
                        icon={<i className="fa fa-refresh"/>}
                        loading={loading}
                        onClick={fetchData}
                        title="Tải lại"
                    />
                    <Button onClick={() => openImportExport('export')}>Xuất dữ liệu</Button>
                    {/* M16 §5.2: không có quyền thì ẩn HẲN nút, không để bấm vào rồi mới báo lỗi. */}
                    {canImport && (
                        <Button onClick={() => openImportExport('import')}>Nhập dữ liệu</Button>
                    )}
                    <Button onClick={openLookup}>Tra cứu theo SKU</Button>
                </div>
            </div>

            {error
                ? <LoadError error={error} message="Không tải được danh sách công đoạn. Vui lòng thử lại" onRetry={fetchData}/>
                : renderWarnings()}

            {summary && summary.computedAt && (
                <div className="VcComputedAt">
                        {/*
                          * Nhãn múi giờ chỉ in khi mốc ĐỌC ĐƯỢC. `timezoneLabelAt` luôn trả một
                          * nhãn — đường lui là nhãn của MÁY — nên dán nó cạnh một mốc đã hiện `—`
                          * là nói dối kiểu im lặng: người xem đọc "tính theo giờ Chatham" trong
                          * khi chẳng có mốc nào, và nhãn tự đổi khi qua kỳ DST mà màn không đổi gì.
                          */}
                        Dữ liệu định mức tính lúc {formatDateTime(summary.computedAt)}
                        {hasReadableOffset(summary.computedAt) && ` (${timezoneLabelAt(summary.computedAt)})`}
                    </div>
            )}

            <Table
                className="VcTable VcStageTable"
                rowKey="stageCode"
                columns={columns}
                dataSource={stages}
                loading={loading}
                pagination={false}
                size="middle"
                // Tổng bề rộng cột = 1479. Khai scroll.x để bảng tự cuộn ngang trong khung của nó,
                // thay vì tràn ra ngoài làm lệch hàng tiêu đề như trước.
                scroll={{x: 1479}}
                locale={{emptyText: 'Chưa có công đoạn nào trong danh mục Phương pháp sản xuất'}}
                // KHÔNG gắn onRow.onClick: chỉ nút "Cấu hình" mới dẫn sang màn cấu hình. Cả dòng
                // bấm được thì mọi thao tác đọc trên dòng — quét mắt theo hàng, bôi đen một mã
                // action để copy — đều thành một cú điều hướng ngoài ý muốn, mà bảng này là bảng
                // để TRA CỨU nhiều hơn là để đi tiếp.
                rowClassName={(row) => {
                    if (row.groupCount) return ''

                    // Chưa cấu hình: nền dòng nhạt màu đỏ. Riêng loại "theo yêu cầu" thì cả dòng mờ
                    // đi, vì thiếu cấu hình ở đó không chặn SKU nào.
                    return row.stageType === 'on_demand' ? 'VcRow--dimmed' : 'VcRow--unconfigured'
                }}
            />

            {/*
              * `destroyOnClose` là bắt buộc, không phải tối ưu: `LookupPage` tự chọn SKU đầu danh
              * mục lúc mount và `ImportExportPage` giữ file đang xem trước ở state. Không huỷ thì
              * lần mở sau hiện lại kết quả của lần trước.
              */}
            <Modal
                visible={lookupOpen}
                footer={null}
                closable={false}
                width="95%"
                destroyOnClose
                zIndex={1050}
                bodyStyle={{paddingTop: 12}}
                onCancel={closeLookup}
            >
                {lookupOpen && (
                    <LookupPage history={history} location={location} onClose={closeLookup}/>
                )}
            </Modal>

            <Modal
                visible={!!ieMode}
                footer={null}
                closable={false}
                width="95%"
                destroyOnClose
                zIndex={1050}
                bodyStyle={{paddingTop: 12}}
                onCancel={closeImportExport}
            >
                {!!ieMode && (
                    <ImportExportPage
                        onClose={closeImportExport}
                        mode={ieMode}
                        onModeChange={changeIeMode}
                    />
                )}
            </Modal>
        </div>
    )
}

export default StageListPage
